import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  // ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  Button,
  IconButton,
  Typography,
  // Divider,
  Stack,
  // Switch,
  // FormControlLabel,
  CircularProgress,
  Alert,
  // ListItemSecondaryAction,
  Box,
  DialogActions,
  DialogContentText,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { 
  fetchNotes, 
  createNote, 
  updateNote, 
  deleteNote,
  clearNotesError,
  clearNotesLoading,
} from '../../features/notes/notesSlice';

export default function NotesDialog({ open, onClose, classId }) {
  const dispatch = useDispatch();
  const { items: notes, loading, error } = useSelector(state => state.notes);
  const { token, user } = useSelector(state => state.auth);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showOnlyMyNotes, setShowOnlyMyNotes] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const { loadingActions } = useSelector((state) => state.techniques);
  const isLoading = loading || loadingActions[`notes_${classId}`];

  // 首先按 class 过滤笔记
  const currentClassNotes = (notes[classId] || []).filter(note => 
    note.class_item === parseInt(classId)
  );
  
  // 获取当前用户在该 class 的笔记数量
  const myNotesCount = currentClassNotes.filter(note => 
    note.username === user?.username
  ).length;
  
  // 然后根据用户选择过滤笔记
  const filteredNotes = showOnlyMyNotes 
    ? currentClassNotes.filter(note => note.username === user?.username)
    : currentClassNotes;

  useEffect(() => {
    if (classId) {
      dispatch(fetchNotes(classId));
    }
  }, [dispatch, classId]);

  const handleCreateNote = async () => {
    try {
      if (!newNoteContent.trim() || !token) return;

      await dispatch(createNote({ 
        content: newNoteContent.trim(),
        classId: parseInt(classId)  // 确保 classId 是数字
      })).unwrap();

      setNewNoteContent('');
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const handleEditClick = (note) => {
    setEditingNote(note.id);
    setEditContent(note.content);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editContent.trim() || !token) return;  // 检查 token
      
      console.log('Starting note update...', {  // 调试日志
        noteId: editingNote,
        content: editContent.trim(),
        classId: classId
      });

      const result = await dispatch(updateNote({ 
        noteId: editingNote, 
        content: editContent.trim(),
        classId: classId
      })).unwrap();
      
      console.log('Update result:', result);  // 调试日志
      
      setEditingNote(null);
      setEditContent('');
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditContent('');
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteNote({ 
        noteId: noteToDelete.id, 
        classId: parseInt(classId) 
      })).unwrap();
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  // 确保切换按钮的文本更清晰
  const toggleButtonText = showOnlyMyNotes 
    ? "Show All Notes" 
    : "Show Only My Notes";

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Notes for Class {classId}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({myNotesCount} notes)
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          {isLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
        </DialogTitle>
        <DialogContent>
          {error && typeof error === 'string' ? (
            <Alert 
              severity="error" 
              onClose={() => dispatch(clearNotesError())}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          ) : error && typeof error === 'object' ? (
            <Alert 
              severity="error" 
              onClose={() => dispatch(clearNotesError())}
              sx={{ mb: 2 }}
            >
              {Object.values(error).join(', ')}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              placeholder="Add a new note..."
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button 
              variant="contained" 
              onClick={handleCreateNote}
              disabled={!newNoteContent.trim() || loading}
              sx={{ mb: 2 }}
            >
              Add Note
            </Button>

            <Button
              onClick={() => setShowOnlyMyNotes(!showOnlyMyNotes)}
              startIcon={showOnlyMyNotes ? <GroupIcon /> : <PersonIcon />}
              sx={{ mb: 2 }}
            >
              {toggleButtonText}
            </Button>

            {loading ? (
              <CircularProgress />
            ) : filteredNotes.length === 0 ? (
              <Alert severity="info">No notes yet. Be the first to add one!</Alert>
            ) : (
              <List>
                {filteredNotes.map((note) => (
                  <ListItem
                    key={note.id}
                    divider
                    secondaryAction={
                      note.username === user?.username && (
                        <Box>
                          {editingNote === note.id ? (
                            <IconButton onClick={handleSaveEdit}>
                              <SaveIcon />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton onClick={() => handleEditClick(note)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                onClick={() => handleDeleteClick(note)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Tooltip 
                        title={note.username}
                        placement="top"
                        arrow
                        PopperProps={{
                          sx: {
                            '& .MuiTooltip-tooltip': {
                              bgcolor: 'background.paper',
                              color: 'text.primary',
                              boxShadow: 1,
                              p: 1,
                              fontSize: '0.875rem',
                              borderRadius: 1
                            },
                            '& .MuiTooltip-arrow': {
                              color: 'background.paper'
                            }
                          }
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: note.user === user?.id ? 'primary.main' : 'grey.500',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.9
                            }
                          }}
                        >
                          {note.username?.[0]?.toUpperCase() || 'A'}
                        </Avatar>
                      </Tooltip>
                    </ListItemAvatar>
                    {editingNote === note.id ? (
                      <TextField
                        fullWidth
                        multiline
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        variant="outlined"
                      />
                    ) : (
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">
                          {note.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By {note.username || 'Anonymous'} • {new Date(note.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this note? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}